import React, {
  ReactElement,
  useState,
  Dispatch,
  SetStateAction,
  memo,
  useCallback,
} from 'react'
import ReactDOM from 'react-dom'
import Helmet from 'react-helmet'
import './Modal.scss'
import { FacebookShareButton, TwitterShareButton } from 'react-share'
import CloseIcon from '../../assets/svg/close.svg'
import fbIcon from '../../assets/images/news-images/fb.png'
import twitterIcon from '../../assets/svg/twitter.svg'
import messageIcon from '../../assets/images/news-images/message.png'
import shareIcon from '../../assets/images/news-images/share.png'

type UseModalReturnTypes = {
  isModalShowing: boolean
  setIsModalShowing: Dispatch<SetStateAction<boolean>>
  handleModalOpen: () => void
  handleModalClose: () => void
}

export const useModal = (defaultIsShowing?: boolean): UseModalReturnTypes => {
  const [isModalShowing, setIsModalShowing] = useState<boolean>(
    defaultIsShowing || false,
  )

  const handleModalOpen = (): void => {
    setIsModalShowing(true)
  }

  const handleModalClose = (): void => {
    setIsModalShowing(false)
  }

  return {
    isModalShowing,
    setIsModalShowing,
    handleModalOpen,
    handleModalClose,
  }
}

type ModalClosePropTypes = {
  onClose: () => void
  className?: string
  fullScreen?: boolean
}

export type ModalDisplayTypes = {
  isShowing: boolean
} & ModalClosePropTypes

type ModalPropTypes = {
  shareUrl?: string
  noIcons?: boolean
  side?: boolean
  displayClose?: boolean
  noPadding?: boolean
  className?: string
  fullScreen?: boolean
  children?:
    | string
    | Element
    | ReactElement
    | boolean
    | (string | Element | ReactElement | boolean)[]
  isShowing?: boolean
} & ModalClosePropTypes

const ModalClose = ({
  onClose,
  className,
  fullScreen,
}: ModalClosePropTypes): ReactElement => (
  <button
    type="button"
    className="modal-close-button"
    data-dismiss="modal"
    aria-label="Close"
    onClick={onClose}
  >
    <img src={CloseIcon} alt="&times;" aria-hidden />
  </button>
)

export const ModalCloseButton = memo(ModalClose)

const Modal = ({
  shareUrl,
  noIcons,
  side,
  isShowing,
  onClose,
  className,
  fullScreen,
  displayClose = true,
  noPadding,
  children,
}: ModalPropTypes): ReactElement => {
  const copyUrl = useCallback(
    (e: React.MouseEvent): void => {
      e.preventDefault()

      navigator.clipboard.writeText(shareUrl || '')
    },
    [shareUrl],
  )

  if (isShowing)
    return ReactDOM.createPortal(
      <>
        <div className="modal-overlay" />
        <div
          className="modal-wrapper"
          aria-modal
          aria-hidden
          tabIndex={-1}
          role="dialog"
        >
          <Helmet>
            <body className="modal-open" />
          </Helmet>
          <div
            className={`modal${side ? ' side' : ''}${
              noPadding ? ' no-padding' : ''
            }`}
          >
            {!!displayClose && (
              <div className="modal-header">
                <button
                  type="button"
                  className={
                    className
                      ? `${className} modal-close-button`
                      : 'modal-close-button'
                  }
                  data-dismiss="modal"
                  aria-label="Close"
                  onClick={onClose}
                />
                {className && !noIcons ? (
                  <div className="icons">
                    <FacebookShareButton url={shareUrl || ''}>
                      <img src={fbIcon} alt="&times;" aria-hidden />
                    </FacebookShareButton>
                    <TwitterShareButton url={shareUrl || ''}>
                      <img src={twitterIcon} alt="&times;" aria-hidden />
                    </TwitterShareButton>
                    <a href={`mailto:?body=${shareUrl}`}>
                      <img src={messageIcon} alt="&times;" aria-hidden />
                    </a>
                    <button type="button" onClick={copyUrl}>
                      <img src={shareIcon} alt="&times;" aria-hidden />
                    </button>
                  </div>
                ) : null}
              </div>
            )}
            {children}
          </div>
        </div>
      </>,
      document.body,
    )

  return <div />
}

export default memo(Modal)
